import React from 'react';
import { Box, Button, Checkbox, lighten, Tooltip, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { InformationTableRow } from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';

type DossierFormaliteProcessListRowProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
};

type DossierFormaliteProcessListRowsProps = {
    formalities: DossierFormaliteProcessListRowProps[];
    selectedFormalitiesIds: number[];
    setSelectedFormalitiesIds: React.Dispatch<React.SetStateAction<number[]>>;
    isHovered?: boolean;
};
const DossierFormaliteProcessListRows: React.FunctionComponent<DossierFormaliteProcessListRowsProps> =
    function (props) {
        const { formalities, selectedFormalitiesIds, setSelectedFormalitiesIds, isHovered } = props;
        const { palette } = useTheme();
        const { enqueueSnackbar } = useSnackbar();

        const handleCheckRow = (
            e: React.ChangeEvent<HTMLInputElement>,
            formalite: DossierFormaliteProcessListRowProps,
        ) => {
            if (e.target.checked) {
                if (formalities.length === selectedFormalitiesIds.length + 1) {
                    enqueueSnackbar("Vous ne pouvez pas sélectionner l'ensemble des formalités", {
                        variant: 'error',
                    });
                } else {
                    setSelectedFormalitiesIds((ids) => [...ids, formalite.id]);
                }
            } else {
                setSelectedFormalitiesIds(
                    selectedFormalitiesIds.filter((id) => id !== formalite.id),
                );
            }
        };

        const iconWrapperSx = {
            display: 'flex',
            alignItems: 'center',
            columnGap: '4px',
        };

        const documentCountLabelSx = {
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: 1.8,
            color: lighten(palette.secondary.main, 0.3),
        };

        const iconSx = {
            fontSize: '12px',
            lineHeight: 1,
            color: lighten(palette.secondary.main, 0.3),
        };

        return (
            <Box>
                {formalities.map((formalite, index) => (
                    <InformationTableRow
                        cellsSx={[
                            {
                                width: isHovered ? '5%' : '0px',
                                display: isHovered ? 'table-cell' : 'block',
                            },
                            { width: '5%' },
                            { width: '45%' },
                            { width: isHovered ? '45%' : '50%' },
                        ]}
                        cels={[
                            <Checkbox
                                // eslint-disable-next-line react/no-array-index-key
                                key={`'row'-${index}`}
                                sx={{
                                    transition: 'opacity ease-in-out 300ms',
                                    pointerEvents: isHovered ? 'auto' : 'none',
                                    opacity: isHovered ? 1 : 0,
                                    width: isHovered ? '100%' : '0px',
                                }}
                                checked={!!selectedFormalitiesIds.includes(formalite.id)}
                                onChange={(e) => handleCheckRow(e, formalite)}
                            />,
                            index + 1,
                            formalite.libelle,
                            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}>
                                {formalite.documentsCount > 0 && (
                                    <Box sx={iconWrapperSx}>
                                        <FaOptionIcon
                                            icon={['fas', 'paperclip']}
                                            sx={iconSx}
                                        />
                                        <Typography
                                            variant="l2dcaption"
                                            sx={documentCountLabelSx}
                                        >
                                            {formalite.documentsCount}
                                        </Typography>
                                    </Box>
                                )}
                                {formalite.optionsCount > 0 && (
                                    <Box sx={iconWrapperSx}>
                                        <FaOptionIcon
                                            icon={['fas', 'gear']}
                                            sx={iconSx}
                                        />
                                        <Typography
                                            variant="l2dcaption"
                                            sx={documentCountLabelSx}
                                        >
                                            {formalite.optionsCount}
                                        </Typography>
                                    </Box>
                                )}
                                {formalite.complete && (
                                    <Box sx={iconWrapperSx}>
                                        <FaOptionIcon
                                            icon={['fas', 'pen-field']}
                                            sx={iconSx}
                                        />
                                    </Box>
                                )}
                                {formalite.externalUrl && (
                                    <Box sx={iconWrapperSx}>
                                        <Tooltip title={formalite.externalUrl}>
                                            <Button
                                                variant="text"
                                                onClick={() =>
                                                    formalite?.onExternalUrlClick(
                                                        formalite.externalUrl as unknown as string,
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 'auto',
                                                    width: 'auto',
                                                    padding: '0',
                                                }}
                                            >
                                                <FaOptionIcon
                                                    icon={['fas', 'link']}
                                                    sx={iconSx}
                                                />
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>,
                        ]}
                    />
                ))}
            </Box>
        );
    };

export default DossierFormaliteProcessListRows;
